import React, { useEffect, useState } from 'react';
import {
  Button,
  Divider,
  FormControl,
  Grid2,
  MenuItem,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
} from '@mui/material';
import SortableTableHead, { SortableTableHeadCell } from '../../shared/components/SortableTableHead';
import { makeStyles } from '@mui/styles';
import SearchBox from '../../shared/components/SearchBox';
import ContentRow from './contentRow';
import { ContentResponse, ContentQueryParams, SubCategoryType, Folder } from '../../../data/types/content.interface';
import Toaster from '../../shared/components/Toaster';
import {
  AddIcon,
  CloseIcon,
  DownIcon,
  NotInUseIcon,
  UnusedIcon,
  UpIcon,
  UsedIcon,
} from '../../shared/components/AppIcons';
import ContentDetails from './contentDetails';
import AddRawContent from './addRawContent';
import ConfirmationDialog from '../../shared/components/ConfirmationDialog';
import { isEmpty, isNumber, startCase } from 'lodash';
import Chips from '../../shared/components/Chips';
import FilterModalForContent from '../../shared/components/filterModalForContent';
import ContentsService from '../../content-creation/services/contents.services';
import usePaginatedContent from '../../shared/components/usePaginatedContent';
import AddFolderModal from '../../shared/components/addFolder';
import ThemeService from '../../tags/theme/services/theme.services';
import { TagType } from '../../../data/types/tags.interface';
import { ADD_CONTENT_ALLOWED_ROLES } from '../../shared/constants';
import { useAuthentication } from '../../auth/services/auth.provider';

const defaultSortDirection = 'asc';
const defaultSortColumn = 'filename';

const useStyles = makeStyles({
  header: {
    fontSize: '28px',
    color: '#303340',
  },
  add: {
    height: '40px',
    fontFamily: 'Lato !important',
    fontSize: '18px !important',
    backgroundColor: '#F67570 !important',
    color: '#fff !important',
    '& g path': {
      fill: '#ffffff !important',
    },
    '& .MuiButton-startIcon': {
      margin: '0px !important',
    },
  },
  removeButtonSection: {
    border: '1px solid #E1E3E8',
    background: ' #F1F2F4',
  },
  button: {
    width: '100px',
    height: '40px',
    border: '1px solid #E1E3E8 !important',
    backgroundColor: '#fff  !important',
    borderRadius: '4px',
    color: '#5F6774 !important',
    paddingLeft: '0px !important',
    '& .MuiButton-startIcon': {
      marginRight: '0px !important',
    },
  },
  table: {
    '& .MuiTableHead-root': {
      backgroundColor: '#30343F',
      color: '#fff !important',
      fontSize: '17px',
      '& .MuiCheckbox-root': {
        color: '#fff !important',
        '&:hover': {
          color: '#fff !important',
        },
      },
      '& .MuiTableSortLabel-root.Mui-active': {
        color: '#fff !important',
        '&:hover': {
          color: '#fff !important',
        },
      },
      '& .MuiTableSortLabel-icon': {
        color: '#fff !important',
      },
    },
  },
  clearAll: {
    color: '#30343F !important',
    fontFamily: 'Lato !important',
    fontSize: '12px !important',
    fontStyle: 'normal !important',
    fontWeight: '400 !important',
    lineHeight: 'normal !important',
  },
  type: {
    '&.MuiFormControl-root': {
      margin: '0px',
      backgroundColor: '#fff',
      '& .MuiInputBase-root': {
        border: '0px solid #E1E3E8',
        borderRadius: '4px',
        height: '40px !important',
        fontFamily: 'Lato !important',
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#E1E3E8 !important',
    },
    // Border on hover
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#E1E3E8 !important',
    },
    // Border on focus
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#E1E3E8 !important',
      borderWidth: '1px !important',
    },
    // Remove native input outline
    '& input': {
      outline: 'none !important',
    },
  },
  dropdown: {
    fontSize: 'small !important',
    color: '#303340 !important',
    width: '150px',
    '& .MuiSelect-select': {
      padding: '4px 0 5px !important',
    },
    '& svg': {
      fontSize: '32px',
      '& path': {
        fill: '#303340 !important',
      },
    },
  },
});

const ContentCollection: React.FC = () => {
  const classes = useStyles();
  const { currentUser: user } = useAuthentication();
  const [themes, setThemes] = useState<TagType[]>([]);
  const [columnHeader, setColumnHeader] = useState<SortableTableHeadCell<any>[]>([]);
  const [selectedContents, setSelectedContents] = useState<ContentResponse[]>([]);
  const [isDeleted] = useState(false);
  const [show, setShow] = useState<boolean>(false);
  const [action, setAction] = useState<string>('');
  const [showAlert, setShowAlert] = useState(false);
  const [openAddRawAssetModal, setOpenAddRawAssetModal] = useState(false);
  const [openRawAssetDetailsModal, setOpenRawAssetDetailsModal] = useState(false);
  const [assetId, setAssetId] = useState<number | null>(null);
  const [openFilters, setOpenFilters] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [refresh, setRefresh] = useState(false);
  const [openAddFolderModal, setOpenAddFolderModal] = useState(false);
  const [selectedFolder, setSelectedFolder] = React.useState<Folder[]>([]);
  const [selectedCategory, setSelectedCategory] = React.useState<SubCategoryType[]>([]);
  const [selectedSubCategory, setSelectedSubCategory] = useState<SubCategoryType[]>([]);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [selectedType, setSelectedType] = useState<TagType[]>([]);
  const [usedOrUnused, setUsedOrUnused] = useState<string | undefined>(undefined);
  const [videoType, setVideoType] = React.useState<string>();
  const [selectedThemes, setSelectedThemes] = React.useState<TagType[]>([]);
  const [toasterMessage, setToasterMessage] = useState('');
  const [open, setOpen] = useState(false);
  const [usageType, setUsageType] = useState('');

  const {
    contentCollections,
    listFilterParams,
    setListFilterParams,
    totalCount,
    folderNames,
    categories,
    subCategories,
    contentTypes,
    subFolderNames,
  } = usePaginatedContent({ type: 'raw' });

  const getToaster = (actionString: string) => (
    <Toaster messageType={actionString} show={show} setShow={setShow} message={`${toasterMessage}`} />
  );

  const getAllThemes = async () => {
    const result = await ThemeService.get('theme');
    if (result) {
      setThemes(result);
    }
  };

  const isAdminOrCreativeEditor = () => ADD_CONTENT_ALLOWED_ROLES.includes(user?.role.name || '');

  useEffect(() => {
    setColumnHeader([
      { id: 'filename', label: 'Raw File', numeric: false, sortable: true },
      { id: 'createdAt', label: 'Date', numeric: false, sortable: true },
      { id: 'theme', label: 'Theme', numeric: false, sortable: false },
      { id: 'category', label: 'Category', numeric: false, sortable: false },
      { id: 'subcategory', label: 'Sub Category', numeric: false, sortable: false },
      { id: 'endFolder', label: 'Content Folder', numeric: false, sortable: false },
      { id: 'title', label: 'Title', numeric: false, sortable: true },
      { id: 'edited', label: 'Used', numeric: true, sortable: true },
    ]);
    getAllThemes();
  }, []);

  useEffect(() => {
    if (refresh) {
      setRefresh(false);
      setListFilterParams((prev) => ({ ...prev, page: 1 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  useEffect(() => {
    setSelectedFolder(listFilterParams.folder || []);
    setSelectedCategory(listFilterParams.category || []);
    setSelectedSubCategory(listFilterParams.subCategory || []);
    setStartDate(listFilterParams.startDate || null);
    setEndDate(listFilterParams.endDate || null);
    setSelectedType(listFilterParams.assetType || []);
    setUsedOrUnused(listFilterParams.usedOrUnused || '');
    setVideoType(listFilterParams.videoType || '');
    setSelectedThemes(listFilterParams.themes || []);
  }, [listFilterParams]);

  useEffect(() => {
    if (!openAddRawAssetModal && isNumber(assetId)) {
      setOpenRawAssetDetailsModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openAddRawAssetModal]);

  useEffect(() => {
    if (!isEmpty(toasterMessage)) {
      setShow((prev) => !prev);
    }
  }, [toasterMessage]);

  const onSearchBoxSubmit = (q: string) => {
    const newParams = { ...listFilterParams, q, page: 1 };

    if (isEmpty(q)) {
      const { q: _, ...rest } = newParams;
      setListFilterParams(rest);
    } else {
      setListFilterParams(newParams);
    }
  };

  const handleRequestSort = (_: React.MouseEvent<unknown>, sortField: any) => {
    if (listFilterParams.sort === sortField) {
      const newDirection = listFilterParams.direction === 'asc' ? 'desc' : 'asc';
      setListFilterParams({ ...listFilterParams, direction: newDirection, page: 1 });
    } else {
      setListFilterParams({ ...listFilterParams, sort: sortField, direction: 'asc', page: 1 });
    }
  };

  const handleFilterOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const rowClickHandler = (property: any) => {
    setOpenRawAssetDetailsModal(true);
    setAssetId(property.id);
  };

  const onCheckRowItem = (content: ContentResponse, isChecked: boolean) => {
    if (isChecked) {
      setSelectedContents((existing) => [...existing, content]);
    } else {
      setSelectedContents((existing) => existing.filter((item) => item.id !== content.id));
    }
  };

  const handleSelectAll = (selection: boolean) => {
    setSelectedContents(selection ? contentCollections : []);
  };

  const updateStatus = async () => {
    const ids = selectedContents.map((content) => content.id);
    const result = await ContentsService.bulkUpdate(ids, { usedOrUnused: usageType });
    setShow(true);
    setAction(result);
    getToaster('result');
    if (result === 'success') {
      setToasterMessage(`${selectedContents.length} file usage status updated successfully`);
    } else {
      setToasterMessage('Unable to update the selected files usage status.Please try again');
    }
  };

  const handleConfirm = async () => {
    if (action === 'remove') {
      if (selectedContents.length > 1) {
        await ContentsService.removeContentInBulk(
          selectedContents.map((content) => content.id),
          'raw'
        );
      } else {
        await ContentsService.removeContent(selectedContents[0].id, 'raw');
      }
    } else {
      setOpen(!open);
      updateStatus();
      setListFilterParams((prev) => ({ ...prev, page: 1 }));
    }
    setShow(true);
    setShowAlert(false);
    setSelectedContents([]);
    setListFilterParams((prev) => ({ ...prev, page: 1 }));
  };

  const handleRemove = () => {
    setAction('remove');
    setToasterMessage('Content(s) removed successfully');
    setShowAlert(true);
  };

  const handleAppliedFilters = (params: ContentQueryParams) => {
    setListFilterParams({
      ...params,
      page: 1,
      type: 'raw',
      sort: defaultSortColumn,
      direction: defaultSortDirection,
      q: listFilterParams.q,
    });
    setOpenFilters(false);
  };

  const handleCloseFilters = () => {
    setOpenFilters(false);
    setListFilterParams({
      perPage: 50,
      page: 1,
      type: 'raw',
      sort: defaultSortColumn,
      direction: defaultSortDirection,
    });
  };

  const fetchFilterCount = () => {
    return ContentsService.getAppliedFiltersCount(listFilterParams);
  };

  const updateAll = (newParams: any) => {
    if (newParams.hasOwnProperty('themes')) {
      listFilterParams.themes = newParams?.themes;
    }
    if (newParams.hasOwnProperty('subCategory')) {
      listFilterParams.subCategory = newParams?.subCategory;
    }
    if (newParams.hasOwnProperty('category')) {
      listFilterParams.category = newParams?.category;
    }
    if (newParams.hasOwnProperty('folder')) {
      listFilterParams.folder = newParams?.folder;
    }
    if (newParams.hasOwnProperty('assetType')) {
      listFilterParams.assetType = newParams?.assetType;
    }
    if (newParams.hasOwnProperty('endDate')) {
      listFilterParams.endDate = newParams?.endDate;
    }
    if (newParams.hasOwnProperty('startDate')) {
      listFilterParams.startDate = newParams?.startDate;
    }
    if (newParams.hasOwnProperty('videoType')) {
      listFilterParams.videoType = newParams?.videoType;
    }
    if (newParams.hasOwnProperty('usedOrUnused')) {
      listFilterParams.usedOrUnused = newParams?.usedOrUnused;
    }

    setListFilterParams({
      ...listFilterParams,
      page: 1,
    });
  };

  const handleCancel = () => {
    setSelectedFolder([]);
    setSelectedCategory([]);
    setSelectedSubCategory([]);
    setStartDate(null);
    setEndDate(null);
    setSelectedType([]);
    setUsedOrUnused(undefined);
    setVideoType('');
    setSelectedThemes([]);
    handleCloseFilters();
  };

  const handleChange = (e: SelectChangeEvent) => {
    setShowAlert(true);
    setAction('update');
    setUsageType(e.target.value);
  };

  return (
    <Grid2 container direction="column">
      {!isEmpty(action) && getToaster(action)}

      <Grid2 container justifyContent="space-between" alignItems="center" height="84px">
        <Grid2 container pl="20px">
          <span className={classes.header}>Content Collection</span>
        </Grid2>
        <Grid2 container justifyContent="flex-end" mr="10px">
          <Grid2 container>
            <FilterModalForContent
              open={openFilters}
              anchorEl={anchorEl}
              handleClose={handleCloseFilters}
              handleFilters={handleAppliedFilters}
              list={folderNames || []}
              categories={categories || []}
              subCategories={subCategories || []}
              contentTypes={contentTypes || []}
              fileType="raw"
              themeList={themes}
              topicList={[]}
            />
          </Grid2>
          <Grid2 container>
            <SearchBox
              query={listFilterParams.q}
              onSubmit={onSearchBoxSubmit}
              isFiltersPresent
              filtersCount={fetchFilterCount()}
              width={180}
              openFilters={openFilters}
              setOpenFilters={setOpenFilters}
              setFiltersAnchorEl={handleFilterOpen}
              placeholder="Search RAW files"
            />
          </Grid2>
          {isAdminOrCreativeEditor() && (
            <Grid2 container>
              <Button
                size="small"
                className={classes.add}
                startIcon={<AddIcon viewBox="0 0 42 42" style={{ fontSize: '35px' }} />}
                onClick={() => {
                  setAction('add');
                  setOpenAddFolderModal(true);
                }}
              >
                <span style={{ textTransform: 'none', marginRight: '10px' }}>Add Raw Content</span>
              </Button>
            </Grid2>
          )}
        </Grid2>
      </Grid2>

      {fetchFilterCount() > 0 && listFilterParams && (
        <>
          <Grid2>
            <Divider
              orientation="horizontal"
              flexItem
              sx={{
                backgroundColor: '#E1E3E8',
                paddingBottom: '1px',
              }}
            />
          </Grid2>
          <Grid2 container mb={1} height="45px" display="flex" alignItems="center" padding="10px 10px 10px 20px">
            <Chips
              selectedFolder={selectedFolder}
              setSelectedFolder={setSelectedFolder}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              selectedSubCategory={selectedSubCategory}
              setSelectedSubCategory={setSelectedSubCategory}
              selectedType={selectedType}
              setSelectedType={setSelectedType}
              selectedThemes={selectedThemes}
              setSelectedThemes={setSelectedThemes}
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
              usedOrUnused={usedOrUnused}
              setUsedOrUnused={setUsedOrUnused}
              videoType={videoType}
              setVideoType={setVideoType}
              updateAll={updateAll}
            />

            {fetchFilterCount() > 0 && (
              <Grid2 container>
                <Button size="small" onClick={handleCancel} className={classes.clearAll}>
                  <span style={{ fontSize: '12px', textTransform: 'none' }}>CLEAR ALL</span>
                </Button>
              </Grid2>
            )}
          </Grid2>
        </>
      )}

      {selectedContents.length > 0 && isAdminOrCreativeEditor() && (
        <Grid2 container pl={4} pt={1} pb={1} alignItems="center" className={classes.removeButtonSection}>
          <Grid2 container justifyContent="flex-start">
            <span style={{ fontWeight: '700', color: '#30343F' }}>
              {selectedContents.length}
              {'  '}Selected
            </span>
          </Grid2>
          <Grid2 container ml={5}>
            <Button
              size="small"
              className={classes.button}
              startIcon={<CloseIcon viewBox="10 8 24  25" style={{ fontSize: '25px', marginRight: '4px' }} />}
              onClick={handleRemove}
            >
              <span style={{ fontSize: 'h5', textTransform: 'none' }}>Remove</span>
            </Button>
          </Grid2>
          <Grid2 container ml={5}>
            <FormControl sx={{ m: 1, minWidth: 120, height: '40px' }} className={classes.type}>
              <Select
                value={usageType || 'NotInUse'}
                onChange={handleChange}
                IconComponent={open ? UpIcon : DownIcon}
                className={classes.dropdown}
              >
                <MenuItem key={0} value="NotInUse">
                  <span>
                    <NotInUseIcon viewBox="5 6 32 32" sx={{ fontSize: '32px' }} />
                    Not In Use
                  </span>
                </MenuItem>
                <MenuItem key={1} value="Used">
                  <span>
                    <UsedIcon viewBox="5 6 32 32" style={{ fontSize: '32px' }} />
                    Used
                  </span>
                </MenuItem>
                <MenuItem key={2} value="UnUsed">
                  <span>
                    <UnusedIcon viewBox="5 6 32 32" sx={{ fontSize: '32px' }} />
                    Not Used
                  </span>
                </MenuItem>
              </Select>
            </FormControl>
          </Grid2>
        </Grid2>
      )}

      <TableContainer className={classes.table} sx={{ overflow: 'hidden' }}>
        <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
          <SortableTableHead
            onRequestSort={handleRequestSort}
            order={listFilterParams.direction || defaultSortDirection}
            orderBy={listFilterParams.sort || defaultSortColumn}
            headCells={columnHeader}
            headerVariant="subtitle2"
            checkboxSelection
            setCheckboxSelectAll={handleSelectAll}
            checkboxAction={isDeleted}
            tableAssetType="raw"
          />

          {contentCollections.length > 0 ? (
            <TableBody sx={{ color: '#303340' }}>
              {contentCollections.map((content: any, index: number) => (
                <ContentRow
                  key={`content-${content.id}`}
                  content={content}
                  index={index}
                  isChecked={selectedContents.includes(content)}
                  onRowClicked={() => rowClickHandler(content)}
                  onCheck={(isChecked) => onCheckRowItem(content, isChecked)}
                  isAdminOrCreativeEditor={isAdminOrCreativeEditor()}
                />
              ))}
            </TableBody>
          ) : (
            <TableBody sx={{ color: '#303340' }}>
              <TableRow>
                <TableCell colSpan={8} align="center">
                  You have not yet created any collection. To begin, click the ‘Add Raw File’ button.
                </TableCell>
              </TableRow>
            </TableBody>
          )}
        </Table>
        {totalCount > 0 && (
          <Grid2 container justifyContent="center" mt={2} mb={2}>
            <TablePagination
              component="div"
              count={totalCount}
              page={listFilterParams.page - 1}
              rowsPerPage={listFilterParams.perPage}
              onPageChange={(_, newPage) =>
                setListFilterParams(prev => ({
                  ...prev,
                  page: newPage + 1
                }))
              }
              onRowsPerPageChange={(e) =>
                setListFilterParams(prev => ({
                  ...prev,
                  perPage: parseInt(e.target.value, 10),
                  page: 1
                }))
              }
              sx={{
                '& .MuiTablePagination-toolbar': {
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  flexWrap: 'nowrap',
                },
                '& .MuiTablePagination-spacer': {
                  display: 'none',
                },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  marginTop: 2,
                },
              }}
            />
          </Grid2>
        )}

        {openRawAssetDetailsModal && (
          <ContentDetails
            setOpenRawAssetDetailsModal={setOpenRawAssetDetailsModal}
            id={assetId}
            setRefresh={setRefresh}
            setToasterMessage={setToasterMessage}
            themeList={themes}
          />
        )}

        {openAddRawAssetModal && (
          <AddRawContent
            setOpenAddRawAssetModal={setOpenAddRawAssetModal}
            setAssetId={setAssetId}
            setRefresh={setRefresh}
            folderNames={folderNames}
            subCategories={subCategories}
            contentTypes={contentTypes}
          />
        )}

        {openAddFolderModal && (
          <AddFolderModal
            setOpenAddFolderModal={setOpenAddFolderModal}
            fileType="raw"
            setRefresh={setRefresh}
            folderNames={folderNames}
            subFolderNames={subFolderNames}
            categories={categories}
            subCategories={subCategories}
            contentTypes={contentTypes}
            label="Add Raw Content(s)"
            setToasterMessage={setToasterMessage}
          />
        )}

        {showAlert && (
          <ConfirmationDialog
            fileCount={selectedContents.length}
            action={action}
            value={startCase(usageType)}
            confirm={handleConfirm}
            setShowAlert={setShowAlert}
          />
        )}
      </TableContainer>
    </Grid2>
  );
};

export default ContentCollection;
